import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Eye, 
  Target, 
  FileText, 
  Link2, 
  Layers,
  CheckCircle,
  Lock,
  Activity,
  Code,
  Terminal,
  Github,
  Heart
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import './ClearFrame.css';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const slideLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const features = [
  { icon: Shield, title: 'Reader / Actor Isolation', description: 'Untrusted content ingestion and tool execution are separate sandboxed processes. Indirect prompt injection is eliminated by design.' },
  { icon: Target, title: 'Goal Monitor', description: 'Agents declare their goal at session start. Every tool call is scored for alignment. Drift triggers auto-pause. Operators approve ambiguous calls via dashboard.' },
  { icon: Eye, title: 'Reasoning Transparency Layer', description: 'Every chain-of-thought step is captured as structured, queryable JSON with SHA-256 content hashes. Full session replay available.' },
  { icon: Link2, title: 'HMAC-Chained Audit Log', description: 'Every event is cryptographically chained to the previous. Tampering is detectable via clearframe audit-verify. Full forensic reconstruction possible.' },
  { icon: Activity, title: 'AgentOps Control Plane', description: 'Live REST + WebSocket dashboard. See alignment scores, inspect reasoning traces, approve/block queued calls, and verify audit integrity in real time.' },
  { icon: Lock, title: 'Supply-Chain Trust', description: 'Signed, verified plugins with zero-trust defaults. No unreviewed code can execute — every tool is vetted before deployment.' }
];

const problemPoints = [
  'No Reader/Actor isolation',
  'Plaintext credential storage',
  'No audit trail',
  'No goal concept',
  'No reasoning transparency',
  'No supply-chain trust'
];

const solutionPoints = [
  { icon: Shield, label: 'Sandboxed Isolation' },
  { icon: Lock, label: 'Encrypted Credentials' },
  { icon: FileText, label: 'Tamper-Proof Logs' },
  { icon: Target, label: 'Goal Alignment' }
];

const useCases = [
  {
    num: '1',
    title: 'Enterprise AI Teams',
    description: 'Deploy AI agents in production with full auditability and compliance. Every action is logged, scored, and verifiable.',
    benefits: ['Cryptographic audit trail', 'Goal drift detection', 'Operator approval flows']
  },
  {
    num: '2',
    title: 'AI Safety Researchers',
    description: 'Study agent behaviour with full reasoning transparency. Replay sessions, inspect chain-of-thought, and verify alignment.',
    benefits: ['Structured reasoning logs', 'Session replay', 'SHA-256 content hashes']
  },
  {
    num: '3',
    title: 'Developers & Builders',
    description: 'Build reliable AI agents with guardrails baked in from day one. Open-source, Python-native, and ready in 60 seconds.',
    benefits: ['Python 3.11+ native', 'CLI scaffolding', 'AgentOps dashboard']
  }
];

const ClearFrame = () => {

  return (
    <div className="cf-page">
      <Helmet>
        <title>ClearFrame - Erasys</title>
        <meta
          name="description"
          content="ClearFrame: The open-source AI agent protocol built for full auditability, goal alignment monitoring, and operator control."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="cf-hero-section">
        <div className="cf-hero-content">
          <motion.h1
            className="cf-hero-title"
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            ClearFrame
          </motion.h1>
          <motion.p
            className="cf-hero-tagline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
          >
            AI agents that run inside a glass box, not a black box.
          </motion.p>
          <motion.div
            className="cf-links-wrapper"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: 'easeOut' }}
          >
            <div className="cf-hero-buttons">
              <a
                href="https://clearframedemo.erasys.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="cf-primary-link"
              >
                Try Demo
                <span className="cf-link-arrow">→</span>
              </a>
              <a 
                href="https://github.com/mazenkabboul/ClearFrame.git" 
                target="_blank" 
                rel="noopener noreferrer"
                className="cf-secondary-link"
              >
                <Github size={18} />
                View on GitHub
                <span className="cf-link-arrow">→</span>
              </a>
              <a 
                href="https://ibrahimmukherjee-boop.github.io/ClearFrame/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="cf-secondary-link"
              >
                <FileText size={18} />
                Documentation
                <span className="cf-link-arrow">→</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview Showcase */}
      <section className="cf-hero-showcase">
        <div className="cf-showcase-container">
          <div className="cf-showcase-content">
            <motion.div
              className="cf-showcase-text"
              variants={slideLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
            >
              <div className="cf-section-label">Open-Source Protocol</div>
              <h2 className="cf-showcase-title">Full Auditability. Goal Alignment. Operator Control.</h2>
              <p className="cf-showcase-description">
                ClearFrame is the open-source AI agent protocol built for full auditability, goal alignment monitoring, 
                and operator control. Every reasoning step logged. Every tool call scored. Every credential encrypted.
              </p>
            </motion.div>
            <motion.div
              className="cf-showcase-visual"
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
            >
              <div className="cf-code-block">
                <div className="cf-code-header">
                  <span className="cf-code-dot cf-dot-red"></span>
                  <span className="cf-code-dot cf-dot-yellow"></span>
                  <span className="cf-code-dot cf-dot-green"></span>
                  <span className="cf-code-filename">agent.py</span>
                </div>
                <pre className="cf-code-content">
{`from clearframe import AgentSession
from clearframe import GoalManifest

manifest = GoalManifest(
  goal="Search for AI safety papers",
  allow_file_write=False,
  allow_code_execution=False,
)

async with AgentSession(manifest) as session:
  result = await session.call_tool(
    "web_search",
    query="AI safety papers 2026"
  )`}
                </pre>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Stats Section */}
      <section className="cf-feature-section">
        <motion.div
          className="cf-feature-header"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          <h2 className="cf-feature-section-title">Why ClearFrame</h2>
          <p className="cf-feature-section-subtitle">A ground-up redesign around auditability, isolation, and control</p>
        </motion.div>
        <motion.div
          className="cf-feature-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {[
            { stat: '5', label: 'Security Layers', desc: 'Five architectural layers that competing protocols simply don\'t have.' },
            { stat: '0', label: 'Trust Assumptions', desc: 'Zero-trust by default. Every plugin signed, every action verified.' },
            { stat: '60s', label: 'To First Agent', desc: 'From pip install to running agent with full audit in under a minute.' },
            { stat: '100%', label: 'Open Source', desc: 'Fully open-source. Inspect every line. Contribute. Fork. Extend.' }
          ].map((item, i) => (
            <motion.div key={i} className="cf-feature-item" variants={fadeUp} custom={i}>
              <h1>{item.stat}</h1>
              <h3>{item.label}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Features Detail Section */}
      <section className="cf-benefits-section">
        <div className="cf-benefits-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            <div className="cf-section-label-center">Architecture</div>
            <h2 className="cf-benefits-title">Five Layers of Protection</h2>
            <p className="cf-benefits-subtitle">
              ClearFrame is not a patch — it's a ground-up redesign for production AI agents
            </p>
          </motion.div>
          <motion.div
            className="cf-benefits-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div key={i} className="cf-benefit-card" variants={fadeUp} custom={i}>
                  <div className="cf-benefit-icon-wrapper">
                    <div className="cf-benefit-icon">
                      <Icon size={36} color="white" strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3 className="cf-benefit-title">{feature.title}</h3>
                  <p className="cf-benefit-description">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Problem Section (vs OpenClaw/MCP) */}
      <section className="cf-privacy-section">
        <div className="cf-privacy-container">
          <div className="cf-privacy-content">
            <motion.div
              className="cf-privacy-text-content"
              variants={slideLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
            >
              <div className="cf-section-label">The Problem</div>
              <h2 className="cf-privacy-title">OpenClaw & MCP Have the Same Structural Flaw</h2>
              <p className="cf-privacy-description">
                A single unsandboxed process reads untrusted content, feeds it into the model, and executes 
                whatever the model produces. There is no audit trail, no goal concept, and no operator visibility.
              </p>
              <ul className="cf-privacy-list">
                {problemPoints.map((text, i) => (
                  <motion.li
                    key={i}
                    className="cf-privacy-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.15 * i, ease: 'easeOut' }}
                  >
                    <CheckCircle size={24} color="#FF3B30" />
                    <span>{text}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="cf-privacy-icons-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
            >
              {solutionPoints.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div key={i} className="cf-privacy-icon-card" variants={fadeUp} custom={i}>
                    <Icon size={32} />
                    <span>{item.label}</span>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="cf-use-cases">
        <div className="cf-cases-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            <div className="cf-section-label-center-light">Use Cases</div>
            <h2 className="cf-cases-title">Built For</h2>
            <p className="cf-cases-subtitle">Teams that need agents they can trust, audit, and control</p>
          </motion.div>
          <motion.div
            className="cf-cases-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
          >
            {useCases.map((uc, i) => (
              <motion.div key={i} className="cf-case-card" variants={fadeUp} custom={i}>
                <div className="cf-case-number">{uc.num}</div>
                <h3 className="cf-case-title">{uc.title}</h3>
                <p className="cf-case-description">{uc.description}</p>
                {uc.benefits.map((b, j) => (
                  <div key={j} className="cf-case-benefit">{b}</div>
                ))}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Quickstart Section */}
      <section className="cf-quickstart-section">
        <div className="cf-quickstart-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            <div className="cf-section-label-center">Quickstart</div>
            <h2 className="cf-benefits-title">From Zero to Running Agent in 60 Seconds</h2>
            <p className="cf-benefits-subtitle">
              ClearFrame is a standard Python package. Install it, declare your agent's goal, and run.
            </p>
          </motion.div>
          <motion.div
            className="cf-quickstart-steps"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
          >
            <motion.div className="cf-quickstart-step" variants={fadeUp}>
              <div className="cf-step-header">
                <Terminal size={20} />
                <span>Install ClearFrame</span>
              </div>
              <div className="cf-step-code">
                <code>pip install clearframe</code>
              </div>
            </motion.div>
            <motion.div className="cf-quickstart-step" variants={fadeUp}>
              <div className="cf-step-header">
                <Code size={20} />
                <span>Initialise a project</span>
              </div>
              <div className="cf-step-code">
                <code>clearframe init my-agent && cd my-agent</code>
              </div>
            </motion.div>
            <motion.div className="cf-quickstart-step" variants={fadeUp}>
              <div className="cf-step-header">
                <Layers size={20} />
                <span>Declare your agent's goal</span>
              </div>
              <div className="cf-step-code">
                <code>GoalManifest(goal="Search for AI safety papers")</code>
              </div>
            </motion.div>
            <motion.div className="cf-quickstart-step" variants={fadeUp}>
              <div className="cf-step-header">
                <Activity size={20} />
                <span>Run your agent</span>
              </div>
              <div className="cf-step-code">
                <code>python agent.py</code>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cf-platform-section">
        <motion.div
          className="cf-platform-content"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          <motion.div
            className="cf-platform-icon"
            initial={{ scale: 0, rotate: -20 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Heart size={48} />
          </motion.div>
          <h2>Open Source. Free Forever.</h2>
          <p>ClearFrame is fully open-source under a permissive license. Inspect every line, contribute, fork, and extend.</p>
          <motion.div
            className="cf-platform-buttons"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <a 
              href="https://github.com/mazenkabboul/ClearFrame.git" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cf-platform-btn"
            >
              <Github size={20} />
              <span>View on GitHub</span>
            </a>
            <a 
              href="https://ibrahimmukherjee-boop.github.io/ClearFrame/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cf-platform-btn cf-platform-btn-docs"
            >
              <FileText size={20} />
              <span>Read the Docs</span>
            </a>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default ClearFrame;
